import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useState } from "react"
import { useForm } from "react-hook-form"

import { DEFAULT_MATERIAL_REQUIREMENTS } from "../../constants/materialRequirements"
import { useSaveMaterialRequirements } from "../../hooks/useSaveMaterialRequirements"
import { materialRequirementsFormSchema } from "../../schemas/materialRequirements"
import type { MaterialRequirementsFormValues } from "../../types"

import { MaterialRequirementsForm } from "./MaterialRequirementsForm"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { LocationMaterialRequirementsPreference } from "@/graphql/codegen/graphql"

interface EditMaterialRequirementsDialogProps {
  onClose: (shouldRefetch?: boolean) => void
  locationId: string
  initialData: LocationMaterialRequirementsPreference | null
}

export const EditMaterialRequirementsDialog: FC<EditMaterialRequirementsDialogProps> = ({
  onClose,
  locationId,
  initialData,
}) => {
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const { saveMaterialRequirements, isSaving } = useSaveMaterialRequirements()

  const isEditMode = !!initialData
  const dialogTitle = isEditMode ? "Edit Material Requirements" : "Add Material Requirements"

  const defaultValues: MaterialRequirementsFormValues = initialData
    ? {
        allowsRecertifiedMaterial: initialData.allowsRecertifiedMaterial ?? true,
        prefersNewestMaterial: initialData.prefersNewestMaterial ?? false,
        shelfLifeRequired: initialData.shelfLifeRequired ?? false,
        shelfLifeRequirement: initialData.shelfLifeRequirement ?? null,
        changeApprovalRequired: initialData.changeApprovalRequired ?? false,
        prefersFewestLots: initialData.prefersFewestLots ?? false,
        labelConfiguration: initialData.labelConfiguration ?? null,
      }
    : DEFAULT_MATERIAL_REQUIREMENTS

  const {
    handleSubmit,
    formState: { isDirty, isSubmitting, isValid },
    control,
    setValue,
  } = useForm<MaterialRequirementsFormValues>({
    defaultValues,
    resolver: zodResolver(materialRequirementsFormSchema),
    mode: "onChange",
  })

  const handleClose = () => {
    if (isDirty) {
      setShowCancelDialog(true)
    } else {
      onClose()
    }
  }

  const handleCancelDialogClose = () => {
    setShowCancelDialog(false)
  }

  const handleConfirmClose = () => {
    setShowCancelDialog(false)
    onClose()
  }

  const onSubmit = async (data: MaterialRequirementsFormValues) => {
    const success = await saveMaterialRequirements({
      locationId,
      formValues: data,
    })
    if (success) {
      onClose(true)
    }
  }

  return (
    <>
      <Dialog open onClose={handleClose} fullWidth maxWidth='sm' data-testid='material-requirements-dialog'>
        <DialogHeader title={dialogTitle} onClose={handleClose} />

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent className='px-10 pb-1 pt-8'>
            <MaterialRequirementsForm control={control} setValue={setValue} />
          </DialogContent>

          <DialogActions className='justify-between p-8'>
            <Button
              appearance='outlined'
              onClick={handleClose}
              disabled={isSubmitting}
              data-testid='material-requirements-cancel-button'
            >
              Cancel
            </Button>
            <Button
              type='submit'
              disabled={!isValid || !isDirty || isSaving}
              data-testid='material-requirements-save-button'
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {showCancelDialog && <CancelDialog onClose={handleCancelDialogClose} onConfirm={handleConfirmClose} />}
    </>
  )
}
